//-----------------------------------------------------------------------------
// economy
//-----------------------------------------------------------------------------

import { TierInterval } from './interfaces';

export const ORGANIZATION_STRIPE_PRICE_LOOKUP = {
	TEAM1: 'tier2_monthly_team1',
};

export const WALLET_ORG_PLANS_CREDITS: {
	[key in (typeof ORGANIZATION_STRIPE_PRICE_LOOKUP)[keyof typeof ORGANIZATION_STRIPE_PRICE_LOOKUP]]: Record<
		TierInterval,
		number
	>;
} = {
	[ORGANIZATION_STRIPE_PRICE_LOOKUP.TEAM1]: {
		[TierInterval.MONTH]: 10_000_000,
		[TierInterval.YEAR]: 10_000_000, // This is reloaded every month by us instead of stripe
	},
};

export function calculateNextCreditGrantDate(anchorDate: Date, monthsToAdd: number): Date {
	const result = new Date(anchorDate);
	const originalDay = result.getDate();
	result.setMonth(result.getMonth() + monthsToAdd);
	// Handle overflow: Jan 31 + 1 month → Mar 3 → clamp to Feb 28
	if (result.getDate() !== originalDay) {
		result.setDate(0); // Sets to last day of previous month
	}
	return result;
}

export const REFERRAL_CREDIT_REWARD = 15_000;
export const REFERRAL_MAX_CREDITS = 750_000;

export const SIGNUP_CREDIT_GRANT = 12_000;

export const DEFAULT_COST_MULTIPLIER = 10000; // $1 = 10000 in credits

// Token costs in dollars per million tokens
// Format: `${api} ${model}` -> { input: cost per million, output: cost per million }
// For image models: output = cost per image * 1,000,000 (since output_tokens = 1 per image)
export const TOKEN_COSTS: Record<string, { input: number; output: number }> = {
	// Anthropic
	'anthropic claude-sonnet-4-6': { input: 3.0, output: 15.0 },
	'anthropic claude-sonnet-4-5': { input: 3.0, output: 15.0 },
	'anthropic-bedrock claude-sonnet-4-5-20250929': { input: 3.0, output: 15.0 },
	'anthropic-bedrock claude-sonnet-4-5': { input: 3.0, output: 15.0 },
	'anthropic-bedrock claude-sonnet-4-6': { input: 3.0, output: 15.0 },
	'anthropic-vertex claude-sonnet-4-5': { input: 3.0, output: 15.0 },
	'anthropic claude-opus-4-6': { input: 5.0, output: 25.0 },
	'anthropic-vertex claude-opus-4-6': { input: 5.0, output: 25.0 },
	'anthropic-bedrock claude-opus-4-6': { input: 5.0, output: 25.0 },
	'anthropic claude-haiku-4-5-20251001': { input: 1.0, output: 5.0 },

	// Google - Text LLM
	'google-vertex gemini-2.0-flash-001': { input: 0.15, output: 0.6 },
	'google-vertex gemini-2.5-flash': { input: 0.3, output: 2.5 },
	'google-vertex gemini-2.5-pro': { input: 1.25, output: 10.0 },
	'google-genai gemini-2.0-flash-001': { input: 0.15, output: 0.6 },
	'google-genai gemini-2.5-flash': { input: 0.3, output: 2.5 },

	'google-genai gemini-2.5-pro': { input: 1.25, output: 10.0 },

	// OpenRouter
	'openrouter gemini-2.5-flash': { input: 0.3, output: 2.5 },
	'openrouter gemini-2.5-pro': { input: 1.25, output: 10.0 },
	'openrouter claude-sonnet-4.5': { input: 3.0, output: 15.0 },
	'openrouter llama-4-maverick': { input: 0.15, output: 0.6 },

	// Cerebras
	'cerebras llama-3.3-70b': { input: 0.6, output: 0.6 },
	'cerebras gpt-oss-120b': { input: 0.25, output: 0.69 },

	// Image generation models (output = $ per image * 1,000,000 since output_tokens = 1 per image)
	'google-genai gemini-3-pro-image-preview': { input: 0, output: 40_000 }, // $0.04 per image
	'google-genai gemini-2.5-flash-image': { input: 0, output: 20_000 }, // $0.02 per image
	'replicate recraft-ai/recraft-v3': { input: 0, output: 40_000 }, // $0.04 per image
	'replicate google/imagen-3': { input: 0, output: 50_000 }, // $0.05 per image
	'replicate black-forest-labs/flux-schnell': { input: 0, output: 3_000 }, // $0.003 per image
};
